import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  MenuItem,
  Pagination,
  Select,
  TableCell,
  TableFooter,
  TableRow,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import {
  getVetsPerformanceByVetReportData,
  getVetsPerformanceWeeklyReportData,
  getVetsRevenueAllReportData,
  getVetsRevenueReportData,
} from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import {
  scheduleImmediateList,
  serviceTypes,
  typeAppointemnt,
  typeList,
  vetPerformStatus,
  visitTypes,
  yesNoBoolList,
} from "../../../../util/dropList";
import { minutesToHours } from "../../../../util/function";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";
import "./VetPerformance.css";

const tableHdrs = [
  "vetName",
  "appointmentDateTime",
  "petName",
  "caseId",
  "serviceType",
  "visitType",
  "typeOfConsult",
  "modeOfBooking",
  "timeSpent",
  "delay",
  "waitTime",
  "scheduledOrImmediate",
  "rescheduled",
  "status",
  "revenue",
  "rating",
  "cancellationReason",
  "rescheduledReason",
];
const initSearch = {
  vetName: "",
  petName: "",
  serviceType: [],
  consultationMode: [],
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};
const filterByList = [
  "Visit Type",
  "Appt Date",
  "Case ID",
  "Booking Type",
  "Time Spent",
  "Delay",
  "Wait Time",
  "Scheduled/Immediate",
  "Rescheduled",
  "Status",
  "Revenue",
  "Rating",
  "Cancellation Reason",
  "Rescheduling Reason",
];
const dropFields = [
  "Visit Type",
  "Booking Type",
  "Scheduled/Immediate",
  "Rescheduled",
  "Status",
];
const splitRows = [
  "Appt Date",
  "Wait Time",
  "Time Spent",
  "Delay",
  "Revenue",
  "Rating",
];

const VetsRevenueReport = () => {
  const dispatch = useDispatch();
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { vetRevenue, vetRevenueAll, vetPerformanceByVet, vetPerformanceWeekly } =
    useSelector((state) => state?.report);
  const rowsPerPage = 10;

  const [dateRange, setDateRange] = useState("2026-08-04 to 2026-08-10");
  const [selectedVetFilter, setSelectedVetFilter] = useState("All vets");
  const [periodToggle, setPeriodToggle] = useState("Week");

  useEffect(() => {
    dispatch(getVetsRevenueAllReportData());
  }, [dispatch]);

  useEffect(() => {
    onApiCall(1);
  }, [
    periodToggle,
    selectedVetFilter,
    searchValues?.vetName,
    searchValues?.petName,
    searchValues?.serviceType,
    searchValues?.consultationMode,
  ]);

  const handleVetSelectChange = (e) => {
    const val = e.target.value;
    setSelectedVetFilter(val);
    setSearchValues((prev) => ({
      ...prev,
      vetName: val === "All vets" ? "" : val,
    }));
  };

  const handleExportExcel = () => {
    const exportData = (getTableData() || []).map((row) => ({
      Vet: row.vetName || "",
      "Date & time": row.appointmentDateTime || "",
      Pet: row.petName || "",
      Service: row.serviceType || "",
      "Visit Type": row.visitType || "",
      Wait: row.waitTime ? `${row.waitTime} min` : "",
      Revenue: row.revenue ? `₹${row.revenue}` : "",
      Status: row.status || "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(
      exportData.length > 0
        ? exportData
        : [
            { Vet: "Raushan Sharma", Appointments: 410, Revenue: "₹1,80,000" },
            { Vet: "Ryan Desouza", Appointments: 385, Revenue: "₹1,65,000" },
            { Vet: "Aditi Sharma", Appointments: 300, Revenue: "₹1,20,000" },
            { Vet: "Abhishek Madan", Appointments: 275, Revenue: "₹1,10,000" },
            { Vet: "Leena Dalal", Appointments: 240, Revenue: "₹95,000" },
          ],
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vet Performance");
    XLSX.writeFile(workbook, "Vet_Performance_Report.xlsx");
  };

  const handleExportPDF = () => {
    window.print();
  };

  const reportData = vetRevenue?.data || vetRevenue || {};
  const summaryInfo = reportData?.metrics || reportData?.summary || {};

  // Date Range Display Sync
  useEffect(() => {
    if (reportData?.dateRangeDisplay) {
      setDateRange(reportData.dateRangeDisplay);
    }
  }, [reportData?.dateRangeDisplay]);

  const summaryCards = [
    {
      title: "Appointments",
      value:
        summaryInfo?.appointmentsDisplay ??
        (summaryInfo?.appointments !== undefined
          ? `${summaryInfo.appointments}`
          : reportData?.appointments ?? "1,885"),
    },
    {
      title: "Avg time spent",
      value:
        summaryInfo?.avgTimeSpentDisplay ??
        (summaryInfo?.avgTimeSpentMinutes !== undefined
          ? `${Math.round(summaryInfo.avgTimeSpentMinutes)} min`
          : summaryInfo?.avgTimeSpent !== undefined
          ? `${summaryInfo.avgTimeSpent} min`
          : reportData?.avgTimeSpent ?? "18 min"),
    },
    {
      title: "Avg wait time",
      value:
        summaryInfo?.avgWaitTimeDisplay ??
        (summaryInfo?.avgWaitTimeMinutes !== undefined
          ? `${Math.round(summaryInfo.avgWaitTimeMinutes)} min`
          : summaryInfo?.avgWaitTime !== undefined
          ? `${summaryInfo.avgWaitTime} min`
          : reportData?.avgWaitTime ?? "6 min"),
    },
    {
      title: "Avg delay",
      value:
        summaryInfo?.avgDelayDisplay ??
        (summaryInfo?.avgDelayMinutes !== undefined
          ? `${Math.round(summaryInfo.avgDelayMinutes)} min`
          : summaryInfo?.avgDelay !== undefined
          ? `${summaryInfo.avgDelay} min`
          : reportData?.avgDelay ?? "4 min"),
    },
    {
      title: "Revenue",
      value:
        summaryInfo?.revenueDisplay ??
        (summaryInfo?.revenue !== undefined
          ? `₹${summaryInfo.revenue}`
          : reportData?.revenue ?? "₹4.82L"),
    },
    {
      title: "Avg rating",
      value:
        summaryInfo?.avgRatingDisplay ??
        (summaryInfo?.avgRating !== null && summaryInfo?.avgRating !== undefined
          ? `${summaryInfo.avgRating}`
          : reportData?.avgRating ?? "-"),
    },
    {
      title: "Cancellation rate",
      value:
        summaryInfo?.cancellationRateDisplay ??
        (summaryInfo?.cancellationRate !== undefined
          ? `${summaryInfo.cancellationRate}%`
          : reportData?.cancellationRate ?? "3.2%"),
    },
    {
      title: "Reschedule rate",
      value:
        summaryInfo?.rescheduleRateDisplay ??
        (summaryInfo?.rescheduleRate !== undefined
          ? `${summaryInfo.rescheduleRate}%`
          : reportData?.rescheduleRate ?? "5.1%"),
    },
  ];

  // Left Bar Chart Data (Consultations by Vet)
  const byVetObj =
    vetPerformanceByVet?.data ||
    vetPerformanceByVet ||
    reportData?.byVet ||
    {};

  const byVetList = Array.isArray(byVetObj?.vets)
    ? byVetObj.vets
    : Array.isArray(byVetObj)
    ? byVetObj
    : [];

  let barCategories = [
    "Raushan Sharma",
    "Ryan Desouza",
    "Aditi Sharma",
    "Abhishek Madan",
    "Leena Dalal",
  ];
  let barChartSeries = [
    {
      name: "Consultations",
      data: [410, 385, 300, 275, 240],
    },
  ];

  if (byVetList.length > 0) {
    barCategories = byVetList.map(
      (item) => item?.doctorName || item?.vetName || item?.name || "",
    );
    barChartSeries = [
      {
        name: "Consultations",
        data: byVetList.map((item) =>
          Number(item?.consultations ?? item?.count ?? item?.total ?? 0),
        ),
      },
    ];
  }

  const barChartTitle =
    byVetObj?.title ||
    "Consultations — all vets (click a bar to filter table)";

  const barChartOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          const selectedIndex = config.dataPointIndex;
          if (selectedIndex >= 0 && barCategories[selectedIndex]) {
            const vetName = barCategories[selectedIndex];
            setSelectedVetFilter(vetName);
            setSearchValues((prev) => ({
              ...prev,
              vetName: vetName,
            }));
          }
        },
      },
    },
    plotOptions: {
      bar: {
        columnWidth: "40%",
        borderRadius: 4,
      },
    },
    colors: ["#005EB8"],
    dataLabels: { enabled: false },
    xaxis: {
      categories: barCategories,
      labels: {
        style: {
          fontFamily: "Montserrat, sans-serif",
          fontSize: "12px",
          colors: "#616161",
        },
      },
    },
    yaxis: {
      min: 0,
      max: 450,
      tickAmount: 9,
      labels: {
        style: {
          fontFamily: "Montserrat, sans-serif",
          fontSize: "12px",
          colors: "#616161",
        },
      },
    },
    grid: {
      borderColor: "#e5e7eb",
      strokeDashArray: 4,
    },
    tooltip: {
      y: {
        formatter: (val) => `${val} consultations`,
      },
    },
  };

  // Right Line Chart Data (Weekly Consultations, Compared)
  const weeklyObj =
    vetPerformanceWeekly?.data ||
    vetPerformanceWeekly ||
    reportData?.weekly ||
    {};

  let lineCategories = [
    "W16",
    "W17",
    "W18",
    "W19",
    "W20",
    "W21",
    "W22",
    "W23",
    "W24",
    "W25",
    "W26",
    "W27",
  ];
  let lineChartSeries = [
    {
      name: "Raushan",
      data: [12, 14, 22, 18, 15, 17, 20, 19, 24, 16, 21, 23],
    },
    {
      name: "Ryan",
      data: [8, 10, 13, 11, 9, 12, 14, 13, 15, 10, 12, 14],
    },
    {
      name: "Aditi",
      data: [6, 7, 9, 8, 7, 8, 10, 9, 11, 7, 9, 10],
    },
  ];

  const weeklyWeeks =
    weeklyObj?.weeks ||
    weeklyObj?.categories ||
    weeklyObj?.periods ||
    [];
  const weeklySeries = Array.isArray(weeklyObj?.series) ? weeklyObj.series : [];

  if (Array.isArray(weeklyWeeks) && weeklyWeeks.length > 0) {
    lineCategories = weeklyWeeks;
  }
  if (weeklySeries.length > 0) {
    lineChartSeries = weeklySeries.map((item) => ({
      name: item?.doctorName || item?.name || item?.vetName || "Vet",
      data: Array.isArray(item?.data) ? item.data : [],
    }));
  }

  const lineChartTitle =
    weeklyObj?.title || "Weekly consultations, compared";

  const lineChartOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: {
      curve: "smooth",
      width: 2.5,
    },
    colors: ["#1976D2", "#E65100", "#2E7D32", "#9C27B0", "#FFC225"],
    markers: {
      size: 0,
    },
    xaxis: {
      categories: lineCategories,
      labels: {
        style: {
          fontFamily: "Montserrat, sans-serif",
          fontSize: "12px",
          colors: "#616161",
        },
      },
    },
    yaxis: {
      min: 0,
      max: 25,
      tickAmount: 5,
      labels: {
        style: {
          fontFamily: "Montserrat, sans-serif",
          fontSize: "12px",
          colors: "#616161",
        },
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
      fontFamily: "Montserrat, sans-serif",
      fontSize: "12px",
      markers: {
        width: 10,
        height: 10,
        radius: 2,
      },
    },
    grid: {
      borderColor: "#e5e7eb",
      strokeDashArray: 4,
    },
  };

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = {
      type: "revenue",
      page: currentPage,
      itemSize: rowsPerPage,
    };
    if (reset) return reqObj;
    if (searchValues?.vetName?.length > 0) {
      reqObj.vetName = [searchValues?.vetName];
    }
    if (searchValues?.petName?.length > 0) {
      reqObj.petName = [searchValues?.petName];
    }
    if (searchValues?.serviceType?.length > 0) {
      reqObj.serviceType = searchValues?.serviceType?.map((li) => li?.value);
    }
    if (searchValues?.consultationMode?.length > 0) {
      reqObj.appointmentType = searchValues?.consultationMode?.map(
        (li) => li?.value,
      );
    }

    if (filterValues?.selectedList?.includes("Visit Type")) {
      reqObj.visitType = filterValues?.values?.["Visit Type"]?.map(
        (li) => li?.value,
      );
    }
    if (filterValues?.selectedList?.includes("Booking Type")) {
      reqObj.visitType = filterValues?.values?.["Booking Type"]?.map(
        (li) => li?.value,
      );
    }
    if (
      filterValues?.selectedList?.includes("Case ID") &&
      filterValues?.values?.["Case ID"]
    ) {
      reqObj.caseId = [filterValues?.values?.["Case ID"]];
    }
    if (filterValues?.selectedList?.includes("Scheduled/Immediate")) {
      reqObj.immediate = filterValues?.values?.["Scheduled/Immediate"]?.map(
        (li) => li?.value,
      );
    }
    if (filterValues?.selectedList?.includes("Appt Date")) {
      reqObj.startDate = moment(
        new Date(filterValues?.values?.["Appt Start Date"] ?? new Date()),
      ).format("YYYY-MM-DD");
      reqObj.endDate = moment(
        new Date(filterValues?.values?.["Appt End Date"] ?? new Date()),
      ).format("YYYY-MM-DD");
    }
    if (filterValues?.selectedList?.includes("Wait Time")) {
      reqObj.minWaitTimeMinutes = Number(
        filterValues?.values?.["Min Wait Time"] ?? 0,
      );
      reqObj.maxWaitTimeMinutes = Number(
        filterValues?.values?.["Max Wait Time"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Time Spent")) {
      reqObj.minWaitTimeMinutes = Number(
        filterValues?.values?.["Min Time Spent"] ?? 0,
      );
      reqObj.maxWaitTimeMinutes = Number(
        filterValues?.values?.["Max Time Spent"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Revenue")) {
      reqObj.minRevenue = Number(filterValues?.values?.["Min Revenue"] ?? 0);
      reqObj.maxRevenue = Number(filterValues?.values?.["Max Revenue"] ?? 0);
    }
    if (filterValues?.selectedList?.includes("Delay")) {
      reqObj.minDelayMinutes = Number(filterValues?.values?.["Min Delay"] ?? 0);
      reqObj.maxDelayMinutes = Number(filterValues?.values?.["Max Delay"] ?? 0);
    }
    if (filterValues?.selectedList?.includes("Rating")) {
      reqObj.minRating = Number(filterValues?.values?.["Min Rating"] ?? 0);
      reqObj.maxRating = Number(filterValues?.values?.["Max Rating"] ?? 0);
    }
    if (filterValues?.selectedList?.includes("Rescheduled")) {
      reqObj.rescheduled = filterValues?.values?.["Rescheduled"]?.map(
        (li) => li?.value,
      );
    }
    if (filterValues?.selectedList?.includes("Status")) {
      reqObj.status = filterValues?.values?.["Status"]?.map((li) => li?.value);
    }
    if (filterValues?.selectedList?.includes("Cancellation Reason")) {
      reqObj.cancelledReason = [
        filterValues?.values?.["Cancellation Reason"] ?? "",
      ];
    }
    if (filterValues?.selectedList?.includes("Rescheduling Reason")) {
      reqObj.rescheduledReason = [
        filterValues?.values?.["Rescheduling Reason"] ?? "",
      ];
    }
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    const filterObj = getFilterObj(pge, reset);
    const periodParam = (periodToggle || "Week").toLowerCase();
    filterObj.period = periodParam;

    if (selectedVetFilter && selectedVetFilter !== "All vets") {
      filterObj.vetName = selectedVetFilter;
    }

    dispatch(getVetsRevenueReportData(filterObj));

    const chartParams = { period: periodParam };
    if (selectedVetFilter && selectedVetFilter !== "All vets") {
      chartParams.vetName = selectedVetFilter;
    }

    dispatch(getVetsPerformanceByVetReportData(chartParams));
    dispatch(getVetsPerformanceWeeklyReportData(chartParams));
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({
      ...searchValues,
      [name?.trim()?.replace(/,$/, "")]: value,
    });
  };

  const onReset = () => {
    setSelectedVetFilter("All vets");
    flushSync(() => {
      setSearchValues(initSearch);
    });
    flushSync(() => {
      setFilterValues({ ...initFilter, selectedList: [], values: {} });
    });
    onApiCall(1, true);
  };

  const onFilterChange = (name, value) => {
    const reqObj = { ...filterValues, [name]: value };
    setFilterValues(reqObj);
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find(
        (tblHdr) => tblHdr?.name === pd,
      );
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => {
    setEditColModVsble(false);
  };

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders
      ?.filter((act) => act?.isSelected)
      ?.map((act) => {
        return act?.name;
      });
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const getPerformanceReportDataList = (vetRevData) => {
    if (Array.isArray(vetRevData?.tableData)) return vetRevData.tableData;
    const payload = vetRevData?.data || vetRevData;
    if (Array.isArray(payload?.roster)) return payload.roster;
    if (Array.isArray(payload?.vets)) return payload.vets;
    if (Array.isArray(payload?.appointments)) return payload.appointments;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.byVet)) return payload.byVet;
    if (Array.isArray(payload)) return payload;
    return [];
  };

  const rawTableList = getPerformanceReportDataList(vetRevenue);
  const totalRecords =
    reportData?.totalRecords ??
    reportData?.totalCount ??
    vetRevenue?.totalRecords ??
    rawTableList.length;
  const totalPage = Math.ceil(totalRecords / rowsPerPage);

  const handleChangePage = (e, selectedPage) => {
    onApiCall(selectedPage);
  };

  const getTableData = () => {
    return rawTableList.map((li) => {
      return {
        ...li,
        vetName: li?.vetName || li?.doctorName || li?.name || "",
        typeOfConsult:
          li?.typeOfConsult === "Home" ? "Home Visit" : li?.typeOfConsult,
        appointmentDateTime:
          li?.appointmentDateTime?.length > 0
            ? moment(new Date(li?.appointmentDateTime)).format(
                "DD MMM YYYY, HH:mm",
              )
            : li?.appointmentDateTime || null,
        revenue: li?.revenue !== undefined ? Number(li?.revenue ?? 0)?.toFixed(2) : "",
        rescheduled: (li?.rescheduled ?? "")?.toString(),
        delay: (li?.delayMinutes ?? "")?.toString()?.length
          ? minutesToHours(li?.delayMinutes)
          : li?.delay || "",
      };
    });
  };

  const handleOpenChart = () => {
    console.log("Log triggered");
  };

  const getVetList = () => {
    const dataList = getPerformanceReportDataList(vetRevenueAll);
    const reqList = dataList
      ?.map((li) => li?.vetName || li?.doctorName)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const getPetList = () => {
    const dataList = getPerformanceReportDataList(vetRevenueAll);
    const reqList = dataList
      ?.map((li) => li?.petName || li?.name)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const getCaseIds = () => {
    const dataList = getPerformanceReportDataList(vetRevenueAll);
    const reqList = dataList
      ?.map((li) => li?.caseId?.toString())
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const onCloseFilter = () => {
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    const reqObj = {
      ...filterValues,
      values: {
        ...filterValues?.values,
        [name?.trim()?.replace(/,$/, "")]: value,
      },
    };
    setFilterValues(reqObj);
  };

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) {
      reqList = reqList?.filter((item) => item !== name);
    } else {
      reqList.push(name);
    }
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onFilterAdd = (value) => {
    const reqObj = { ...filterValues, added: value };
    setFilterValues(reqObj);
  };

  const onFilterReset = () => {
    const reqObj = {
      ...initFilter,
      visible: true,
      selectedList: [],
      values: {},
    };
    setFilterValues(reqObj);
  };

  const onApplyFilter = () => {
    onApiCall();
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const getSuggestionList = (typ = null) => {
    if (!typ) return [];
    if (typ === "Case ID") return getCaseIds();
    const reqType =
      typ === "Cancellation Reason"
        ? "cancelledReason"
        : typ === "Rescheduling Reason"
          ? "rescheduledReason"
          : "";
    const reqList =
      (vetRevenueAll?.data ?? [])
        ?.map((li) => li?.[reqType] ?? "")
        ?.filter(Boolean) ?? [];
    const uniqList = uniq(reqList ?? []);
    return uniqList ?? [];
  };

  const getDropListByType = (typ) => {
    return typ === "Visit Type"
      ? visitTypes
      : typ === "Booking Type"
        ? typeAppointemnt
        : typ === "Scheduled/Immediate"
          ? scheduleImmediateList
          : typ === "Rescheduled"
            ? yesNoBoolList
            : typ === "Status"
              ? vetPerformStatus
              : [];
  };

  const getFieldName = (name, type) => {
    if (name === "Appt Date") {
      if (type === "start") return "Appt Start Date";
      return "Appt End Date";
    }
    if (name === "Wait Time") {
      if (type === "start") return "Min Wait Time";
      return "Max Wait Time";
    }
    if (name === "Time Spent") {
      if (type === "start") return "Min Time Spent";
      return "Max Time Spent";
    }
    if (name === "Delay") {
      if (type === "start") return "Min Delay";
      return "Max Delay";
    }
    if (name === "Revenue") {
      if (type === "start") return "Min Revenue";
      return "Max Revenue";
    }
    if (name === "Rating") {
      if (type === "start") return "Min Rating";
      return "Max Rating";
    }
  };

  return (
    <div className="vet-performance-container">
      <h2 className="vet-performance-title">Vet performance</h2>

      <div className="vet-performance-filter-bar">
        <div className="vet-performance-filter-group">
          <div className="vet-performance-date-picker">
            <Select
              size="small"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              sx={{
                height: 36,
                fontSize: 13,
                fontFamily: "Montserrat, sans-serif",
                borderRadius: "6px",
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
              }}
            >
              <MenuItem value="2026-08-04 to 2026-08-10">
                2026-08-04 to 2026-08-10
              </MenuItem>
              <MenuItem value="2026-07-28 to 2026-08-03">
                2026-07-28 to 2026-08-03
              </MenuItem>
              <MenuItem value="2026-07-01 to 2026-07-31">
                2026-07-01 to 2026-07-31
              </MenuItem>
            </Select>
          </div>

          <div className="vet-performance-vet-select">
            <Select
              size="small"
              value={selectedVetFilter}
              onChange={handleVetSelectChange}
              sx={{
                height: 36,
                fontSize: 13,
                fontFamily: "Montserrat, sans-serif",
                borderRadius: "6px",
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
              }}
            >
              <MenuItem value="All vets">All vets</MenuItem>
              <MenuItem value="Raushan Sharma">Raushan Sharma</MenuItem>
              <MenuItem value="Ryan Desouza">Ryan Desouza</MenuItem>
              <MenuItem value="Aditi Sharma">Aditi Sharma</MenuItem>
              <MenuItem value="Abhishek Madan">Abhishek Madan</MenuItem>
              <MenuItem value="Leena Dalal">Leena Dalal</MenuItem>
            </Select>
          </div>
        </div>

        <div className="vet-performance-period-group">
          {["Week", "Month", "Quarter", "Year"].map((item) => (
            <button
              key={item}
              type="button"
              className={`vet-performance-period-btn ${
                periodToggle === item ? "active" : ""
              }`}
              onClick={() => setPeriodToggle(item)}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div className="vet-performance-export-group">
        <button
          type="button"
          className="vet-performance-export-btn"
          onClick={handleExportExcel}
        >
          <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
          Export Excel
        </button>
        <button
          type="button"
          className="vet-performance-export-btn"
          onClick={handleExportPDF}
        >
          <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
          Export PDF
        </button>
      </div>

      <div className="vet-performance-cards-grid">
        {summaryCards.map((card, idx) => (
          <div className="vet-performance-card" key={idx}>
            <div className="vet-performance-card-title">{card.title}</div>
            <div className="vet-performance-card-value">{card.value}</div>
          </div>
        ))}
      </div>

      <div className="vet-performance-charts-grid">
        <div className="vet-performance-chart-card">
          <div className="vet-performance-chart-header">
            {barChartTitle}
          </div>
          <Chart
            options={barChartOptions}
            series={barChartSeries}
            type="bar"
            height={300}
          />
        </div>

        <div className="vet-performance-chart-card">
          <div className="vet-performance-chart-header">
            {lineChartTitle}
          </div>
          <Chart
            options={lineChartOptions}
            series={lineChartSeries}
            type="line"
            height={300}
          />
        </div>
      </div>

      <div className="vet-performance-drilldown-header">
        Drill-down — appointment detail
      </div>

      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              options={getVetList()}
              value={searchValues?.vetName}
              name="vetName"
              handleChange={onSearchChange}
              label="Vet Name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              options={getPetList()}
              value={searchValues?.petName}
              name="petName"
              handleChange={onSearchChange}
              label="Pet Name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={serviceTypes}
              value={searchValues?.serviceType ?? []}
              name="serviceType"
              handleChange={onSearchChange}
              label="Service Type"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={typeList}
              value={searchValues?.consultationMode ?? []}
              name="consultationMode"
              handleChange={onSearchChange}
              label="Consultation Mode"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0
                ? `${filterValues?.selectedList?.length} `
                : ""}
              Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleOpenChart}
          >
            <AutoGraphIcon sx={w20h20BlueIcon} />
          </div>
        </div>
      </div>
      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {totalRecords}
      </Typography>
      <div className="mtMin10">
        <ReportTable
          datas={getTableData()}
          columns={crntTblHeders}
          maxHeight="none"
        />
        <div className="flex1-end mt20 mb30">
          {totalPage > 1 ? (
            <Pagination
              count={totalPage}
              variant="outlined"
              color="primary"
              page={page}
              onChange={handleChangePage}
            />
          ) : null}
        </div>
      </div>

      <CustomModal
        open={editColModVsble}
        onClose={onEditModClose}
        header="Columns"
        modal
        modalWidth={25}
      >
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <div>
              <CustomButton
                text="Save"
                smallBtn
                onClick={handleSaveTableColumn}
              />
            </div>
          </div>
        </div>
      </CustomModal>
      <CustomModal
        open={filterValues?.visible}
        onClose={onCloseFilter}
        header="Filter"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography className="gray38 fs12 txt-regular">
                Filter by:
              </Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <div>
                      <CustomCheckbox
                        checked={filterValues?.selectedList?.includes(li)}
                        onChange={() => onSelectFilter(li)}
                      />
                    </div>
                    <Typography className="txt-regular fs14 black mb5 mlMin15">
                      {li}
                    </Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div
                    className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor"
                    onClick={() => onFilterAdd(false)}
                  >
                    <AddIcon
                      sx={{ color: AppColors.appPrimary, fontSize: 15 }}
                    />
                    <Typography className="font-medium fs12 blue-color ml5">
                      Add Filter
                    </Typography>
                  </div>
                  <div>
                    {filterValues?.selectedList?.map((li) => {
                      return (
                        <div className="mt20">
                          {dropFields?.includes(li) ? (
                            <CustomSelect
                              list={getDropListByType(li)}
                              value={filterValues?.values?.[li] ?? []}
                              name={li}
                              handleChange={onFilterValuesChange}
                              label={li}
                              multiSelectSearchCheck
                            />
                          ) : splitRows?.includes(li) ? (
                            <div className="flex-row-between-align-center">
                              <div className="w45Per">
                                <CustomTextField
                                  placeholder={getFieldName(li, "start")}
                                  fullWidth
                                  name={getFieldName(li, "start")}
                                  handleChange={onFilterValuesChange}
                                  value={
                                    filterValues?.values?.[
                                      getFieldName(li, "start")
                                    ] ?? ""
                                  }
                                  label={getFieldName(li, "start")}
                                  mobileDate={li === "Appt Date"}
                                  type="number"
                                />
                              </div>
                              <div className="w45Per">
                                <CustomTextField
                                  placeholder={getFieldName(li, "end")}
                                  fullWidth
                                  name={getFieldName(li, "end")}
                                  handleChange={onFilterValuesChange}
                                  value={
                                    filterValues?.values?.[
                                      getFieldName(li, "end")
                                    ] ?? ""
                                  }
                                  label={getFieldName(li, "end")}
                                  mobileDate={li === "Appt Date"}
                                  type="number"
                                />
                              </div>
                            </div>
                          ) : (
                            <CustomTextField
                              placeholder={li}
                              fullWidth
                              name={li}
                              handleChange={onFilterValuesChange}
                              value={filterValues?.values?.[li] ?? ""}
                              label={li}
                              suggetions
                              options={getSuggestionList(li)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <div>
                    <CustomButton
                      text="Add"
                      smallBtn
                      onClick={() => onFilterAdd(true)}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <div>
                    <CustomButton
                      text="Reset All"
                      smallBtn
                      onClick={onFilterReset}
                      tabSelectdBtn
                    />
                  </div>
                  <div className="ml20">
                    <CustomButton
                      text="Apply Filters"
                      smallBtn
                      onClick={onApplyFilter}
                    />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </div>
  );
};

export default VetsRevenueReport;
